import { createClient } from '@supabase/supabase-js';
import { config } from '../config';
import type { Database } from './database.types';

export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type Views<T extends keyof Database['public']['Views']> = Database['public']['Views'][T]['Row'];
export type Insert<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Insert'];

if (!config.supabaseUrl || !config.supabaseAnonKey) {
  // Shown in the browser console if the Netlify environment variables are missing.
  console.error('VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not set.');
}

export const supabase = createClient<Database>(config.supabaseUrl, config.supabaseAnonKey, {
  auth: {
    // Stay signed in on this device until "Sign out" (Brett: no two-factor, remember me).
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // 'implicit': a sign-in link works on whichever device opens it (request on the
    // laptop, tap the email on the phone). 'pkce' would only work in the requesting browser.
    flowType: 'implicit',
  },
});

/** Throws Supabase errors so React Query shows them instead of silently returning nothing. */
export function unwrap<T>(result: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (result.error) throw new Error(result.error.message);
  return result.data as NonNullable<T>;
}

/** Like unwrap, for queries where "no row" is a normal answer (maybeSingle). */
export function unwrapMaybe<T>(result: { data: T; error: { message: string } | null }): T | null {
  if (result.error) throw new Error(result.error.message);
  return result.data;
}
